import React, { ForwardedRef } from "react";
import useResizeObserver from "use-resize-observer";
import { Zoom } from "@visx/zoom";

import {
  FeatureShape,
  WorldMapInstance,
  WorldMapProps,
} from "./WorldMap.types";
import { getMercatorProps } from "./WorldMap.helpers";
import WorldMapContent from "./WorldMapContent/WorldMapContent";
import { Tooltip } from "..";

function WorldMap<T>(
  props: WorldMapProps<T>,
  ref: ForwardedRef<WorldMapInstance>
) {
  const {
    width: widthProp,
    height: heightProp,
    getCountryFill,
    getCountryByCode,
    getCountryTooltip,
    zoomAndPan = false,
  } = props;

  const {
    ref: rootRef,
    width = 1,
    height = 1,
  } = useResizeObserver<HTMLDivElement>();

  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const [activeCountry, setActiveCountry] = React.useState<T>();
  const [activeFeature, setActiveFeature] = React.useState<FeatureShape>();

  const anchorRef = React.useRef<SVGCircleElement | null>(null);
  const resetRef = React.useRef<HTMLButtonElement | null>(null);
  const zoomInRef = React.useRef<HTMLButtonElement | null>(null);
  const zoomOutRef = React.useRef<HTMLButtonElement | null>(null);

  const handleCountryMouseOver = React.useCallback(
    (
      event: React.MouseEvent<SVGPathElement>,
      feature: FeatureShape,
      country?: T
    ) => {
      setTooltipOpen(true);
      setActiveCountry(country);
      setActiveFeature(feature);
      const target = event.target as SVGPathElement;
      anchorRef.current = target.parentNode?.querySelector(
        "circle"
      ) as SVGCircleElement;
    },
    []
  );

  const handleCountryMouseOut = React.useCallback(() => {
    setTooltipOpen(false);
  }, []);

  const mercatorProps = React.useMemo(() => {
    return getMercatorProps(width, height);
  }, [height, width]);

  React.useImperativeHandle(ref, () => ({
    reset() {
      resetRef.current?.click();
    },
    zoomIn() {
      zoomInRef.current?.click();
    },
    zoomOut() {
      zoomOutRef.current?.click();
    },
  }));

  const createPanStyle = (zoom: { isDragging: boolean }) => {
    return {
      cursor: zoom.isDragging ? "grabbing" : "grab",
      touchAction: "none",
    };
  };

  const tooltipTitle = getCountryTooltip(activeFeature!, activeCountry) || "";

  return (
    <Zoom<SVGSVGElement>
      width={width}
      height={height}
      scaleXMin={1 / 2}
      scaleXMax={4}
      scaleYMin={1 / 2}
      scaleYMax={4}
    >
      {(zoom) => (
        <Tooltip
          open={tooltipOpen}
          title={tooltipTitle}
          PopperProps={{ anchorEl: anchorRef.current }}
          enterDelay={300}
        >
          <div ref={rootRef} style={{ width: widthProp, height: heightProp }}>
            <svg
              width={width}
              height={height}
              ref={zoomAndPan ? zoom.containerRef : undefined}
              style={zoomAndPan ? createPanStyle(zoom) : undefined}
            >
              <g transform={zoom.toString()}>
                <WorldMapContent<T>
                  scale={mercatorProps.scale}
                  translate={mercatorProps.translate}
                  getCountryFill={getCountryFill}
                  getCountryByCode={getCountryByCode}
                  onCountryMouseOver={handleCountryMouseOver}
                  onCountryMouseOut={handleCountryMouseOut}
                />
              </g>
            </svg>
            <button hidden onClick={() => zoom.reset()} ref={resetRef} />
            <button
              hidden
              onClick={() => zoom.scale({ scaleX: 1.2, scaleY: 1.2 })}
              ref={zoomInRef}
            />
            <button
              hidden
              onClick={() => zoom.scale({ scaleX: 0.8, scaleY: 0.8 })}
              ref={zoomOutRef}
            />
          </div>
        </Tooltip>
      )}
    </Zoom>
  );
}

export default React.forwardRef(WorldMap) as <T>(
  props: WorldMapProps<T> & { ref?: React.ForwardedRef<WorldMapInstance> }
) => ReturnType<typeof WorldMap>;
